import { useCallback, useEffect } from 'react';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import type { SerializedProformaFormData } from 'rest/request-create';
import { ValidationFieldSet } from '@atlassian/proforma-form-renderer/field-validation';
import {
	usePublicFormRendererActions as useFormRendererActions,
	usePublicFormRendererState as useFormRendererState,
} from '@atlassian/proforma-form-renderer/form-renderer-store';
import {
	convertJiraFieldErrors,
	convertValuesToFormAnswers,
} from '@atlassian/proforma-form-renderer/legacy-converters/utils';
import type { ProformaForm } from '@atlassian/proforma-form-renderer/proforma-form';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics/events';

import type { Forms } from '../../../../common/types/forms';
import { buildFormCreatedAnalyticsAttributes } from './utils/form-created-analytics-attributes';
import {
	useRegisterProformaForm,
	useBlockFormSubmission,
	useFieldErrors,
	useSetValidationFieldSet,
} from '../../../../controllers/request-create-context-provider';

/**
 * Hook to register the Proforma form validation and serialization callbacks with the parent Formik form.
 * This is called on mount and whenever the registerProformaForm callback changes.
 */
export const useFormikRegisterProformaForm = (forms: Forms) => {
	di(useAnalyticsEvents, useFormRendererActions, useRegisterProformaForm, useSetValidationFieldSet);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { getFieldIdsFailingValidation, getVisibleFieldValues } = useFormRendererActions();
	const registerProformaForm = useRegisterProformaForm();
	const setValidationFieldSet = useSetValidationFieldSet();

	// Validates the form and returns validity via callback
	const validateProformaForm = useCallback(
		(callback: (isValid: boolean) => void) => {
			// The portal and Jira publish settings are kept in sync by the backend; prefer portal here.
			const portalPublishing = forms.proformaForm.publish?.portal;
			if (portalPublishing === undefined) {
				// Mirror the Jira consumer: without publish config we cannot proceed.
				callback(false);
				return;
			}

			const { validateOnCreate } = portalPublishing;

			if (validateOnCreate) {
				setValidationFieldSet(ValidationFieldSet.ALL_FIELDS);
			} else if (
				getFieldIdsFailingValidation({ fieldSet: ValidationFieldSet.SECURITY_CRITICAL }).length > 0
			) {
				setValidationFieldSet(ValidationFieldSet.SECURITY_CRITICAL);
				callback(false);
				return;
			}

			callback(
				!validateOnCreate ||
					getFieldIdsFailingValidation({ fieldSet: ValidationFieldSet.ALL_FIELDS }).length === 0,
			);
		},
		[forms.proformaForm, getFieldIdsFailingValidation, setValidationFieldSet],
	);

	// Converts FormRenderer field values to the `SerializedProformaFormData` format
	const serializeProformaForm = useCallback(
		(callback: (serializedData: SerializedProformaFormData | undefined) => void) => {
			const visibleValues = getVisibleFieldValues();
			const answers = convertValuesToFormAnswers(
				visibleValues,
				forms.proformaForm.design.questions,
			);

			const serializedFormData: SerializedProformaFormData = {
				answers,
				templateFormId: forms.proformaForm.design.settings.templateId,
				visibleFieldCount: Object.keys(visibleValues ?? {}).length,
			};
			callback(serializedFormData);

			fireTrackAnalytics(
				createAnalyticsEvent({}),
				`${ActionSubject.FORM} ${Action.CREATED}`,
				buildFormCreatedAnalyticsAttributes(forms.proformaForm),
			);
		},
		[createAnalyticsEvent, forms.proformaForm, getVisibleFieldValues],
	);

	// Register the validation and serialization functions with the parent Formik form
	useEffect(() => {
		if (registerProformaForm) {
			registerProformaForm(validateProformaForm, serializeProformaForm);
		}
	}, [registerProformaForm, validateProformaForm, serializeProformaForm]);
};

/**
 * Hook to convert parent fieldErrors (Jira field keys) to FormRenderer field IDs
 * and update the FormRenderer's custom field errors.
 */
export const useFormikFieldErrors = (forms: Forms) => {
	di(useFormRendererActions, useFieldErrors);

	const { setFieldCustomErrors } = useFormRendererActions();
	const fieldErrors = useFieldErrors();

	useEffect(() => {
		if (!fieldErrors) {
			return;
		}

		// Cast TemplateForm to ProformaForm - the uuid field is optional in common-core but required in form-renderer types
		const proformaFieldErrors = convertJiraFieldErrors(
			forms.proformaForm as ProformaForm,
			fieldErrors,
		);
		setFieldCustomErrors(proformaFieldErrors);
	}, [forms.proformaForm, fieldErrors, setFieldCustomErrors]);
};

/**
 * Hook to block the parent Formik form submission while a file upload is in progress.
 * Mirrors the old RenderForm useEffect that called blockFormSubmission(formStore.isUploadingMedia).
 */
export const useFormikBlockSubmissionDuringUpload = () => {
	di(useFormRendererState, useBlockFormSubmission);

	const { isFileUploadInProgress } = useFormRendererState();
	const blockFormSubmission = useBlockFormSubmission();

	useEffect(() => {
		blockFormSubmission(isFileUploadInProgress);
	}, [blockFormSubmission, isFileUploadInProgress]);
};
